import {
  Avatar,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Radio,
  Space,
  Image,
  Switch,
  Modal,
  Select,
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { responseNotification } from "../../utils/notify";
import SubmitResetBtn from "../common/SubmitBtn";
import { CloseOutlined, GiftOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import React from "react";
import axios from "axios";
import { debounce } from "lodash";

type DiscountMode = null | "disFlat" | "disPercentage";

type FreeProductItem = {
  id: string;
  name: string;
  productImage?: string;
};

const QuickUpdate = ({ visibleData, onCloseMethod }: any) => {
  const { type, token } = useSelector((state) => (state as any)?.authReducer);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [discountImageUrl, setDiscountImageUrl] = useState<string[]>([]);
  const [discountTypes, setDiscountTypes] = useState<DiscountMode[]>([]);
  const [basePriceCalculationType, setBasePriceCalculationType] =
    useState<any>();
  const [commissionAmount, setCommissionAmount] = useState<any>();
  const [commissionType, setCommissionType] = useState<any>();
  const [freeProductId, setFreeProductId] = useState<any>("NULL");
  const [productsOptions, setProductsOptions] = useState<any>({
    list: [],
    loading: false,
  });
  const [discountType, setDiscountType] = useState<{
    [key: string]: {
      type: "disFlat" | "disPercentage" | null;
      value: undefined | number;
    };
  }>({});

  const [qtyDiscountType, setQtyDiscountType] = useState<{
    [key: string]: {
      type: "freeProduct" | "qtyFlat" | "qtyPercentage" | null;
      value: undefined | number | string;
      minimumQuantity: number;
    };
  }>({});

  const [singleShopInfo, setSingleShopInfo] = useState<any>({
    loading: false,
    data: null,
  });

  const [categoryData, setCategoryData] = useState<any>(undefined);
  const [catCommissionAmount, setCatCommissionAmount] = useState<any>();
  const [catCommissionType, setCatCommissionType] = useState<any>();

  // row-wise selected free product
  const [selectedFreeProducts, setSelectedFreeProducts] = useState<{
    [key: number]: FreeProductItem | undefined;
  }>({});

  // modal state
  const [freeProductModal, setFreeProductModal] = useState<{
    open: boolean;
    rowIndex: number | null;
  }>({
    open: false,
    rowIndex: null,
  });

  // placeholder options: এখানে API call বসাবে
  const [freeProductOptions, setFreeProductOptions] = useState<
    FreeProductItem[]
  >([
    {
      id: "p1",
      name: "Free Product One",
      productImage: "https://via.placeholder.com/60",
    },
    {
      id: "p2",
      name: "Free Product Two",
      productImage: "https://via.placeholder.com/60",
    },
    {
      id: "p3",
      name: "Free Product Three",
      productImage: "https://via.placeholder.com/60",
    },
  ]);

  const computeSalesPrice = (
    mrp: number,
    flat: number,
    percent: number,
    mode: DiscountMode
  ) => {
    let final = mrp;

    if (mode === "disFlat") {
      final = mrp - flat;
    } else if (mode === "disPercentage") {
      final = mrp - (mrp * percent) / 100;
    } else {
      final = mrp;
    }

    if (Number.isNaN(final) || final < 0) final = 0;
    return final;
  };

  const normalizeCommissionType = (t: any) => {
    const s = String(t ?? "")
      .trim()
      .toUpperCase();

    if (
      s === "PERCENT" ||
      s === "PERCENTAGE" ||
      s === "PERCENTAGE_BASED" ||
      s === "PERCENT_BASED" ||
      s === "RATE"
    ) {
      return "PERCENTAGE";
    }

    if (
      s === "FLAT" ||
      s === "FIXED" ||
      s === "AMOUNT" ||
      s === "FIXED_AMOUNT" ||
      s === "TK"
    ) {
      return "FLAT";
    }

    return "UNKNOWN";
  };

  const computeTpFromSalesPrice = (salesPrice: number) => {
    let commAmt = 0;
    let commTypeRaw: any;

    if (basePriceCalculationType === "SHOP_BASED_COMMISSION") {
      commAmt = Number(commissionAmount) || 0;
      commTypeRaw = commissionType;
    } else if (basePriceCalculationType === "CATEGORY_BASED_COMMISSION") {
      commAmt = Number(catCommissionAmount) || 0;
      commTypeRaw = catCommissionType;
    } else {
      return salesPrice;
    }

    const commType = normalizeCommissionType(commTypeRaw);

    let tp = salesPrice;

    if (commType === "PERCENTAGE") {
      tp = salesPrice - (salesPrice * commAmt) / 100;
    } else if (commType === "FLAT") {
      tp = salesPrice - commAmt;
    } else {
      tp = salesPrice;
    }

    if (Number.isNaN(tp) || tp < 0) tp = 0;
    return tp;
  };

  const recalcRow = (index: number, overrideMode?: DiscountMode) => {
    const variations = [...(form.getFieldValue("variations") || [])];
    if (!variations[index]) return;

    const mrp = parseFloat(variations[index]?.mrp) || 0;
    const flat = parseFloat(variations[index]?.flat) || 0;
    const percent = parseFloat(variations[index]?.percentage) || 0;

    const mode =
      overrideMode !== undefined ? overrideMode : discountTypes[index] ?? null;

    const sales = computeSalesPrice(mrp, flat, percent, mode);

    const nextRow: any = {
      ...variations[index],
      sellerPrice: sales.toFixed(2),
    };

    if (basePriceCalculationType !== "TP_BASED_COMMISSION") {
      nextRow.tp = computeTpFromSalesPrice(sales).toFixed(2);
    }

    variations[index] = nextRow;
    form.setFieldsValue({ variations });
  };

  useEffect(() => {
    if (!form) return;
    if (basePriceCalculationType === "TP_BASED_COMMISSION") return;

    const variations = [...(form.getFieldValue("variations") || [])];
    if (!variations.length) return;

    const next = variations.map((row: any) => {
      const sales = parseFloat(row?.sellerPrice) || 0;
      return {
        ...row,
        tp: computeTpFromSalesPrice(sales).toFixed(2),
      };
    });

    form.setFieldsValue({ variations: next });
  }, [
    basePriceCalculationType,
    commissionAmount,
    commissionType,
    catCommissionAmount,
    catCommissionType,
    form,
  ]);

  const fetchShopDetails = useCallback(
    (shopId: any) => {
      if (!shopId) return;

      try {
        setSingleShopInfo({ loading: true, data: null });
        axios
          .get(
            `${process.env.REACT_APP_CATALOG_READER_API}/merchant-shop/my-shop-details?shopId=${shopId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          )
          .then((data) => {
            if (data.status === 200) {
              setSingleShopInfo({
                loading: false,
                data: data?.data?.shop,
              });

              const shop = data?.data?.shop;
              setBasePriceCalculationType(shop?.basePriceCalculationType);
              setCommissionAmount(shop?.commissionAmount);
              setCommissionType(shop?.commissionType);
            } else {
              setSingleShopInfo({ loading: false, data: null });
              responseNotification(
                data.statusText || "something went wrong",
                "error"
              );
            }
          })
          .catch(() => setSingleShopInfo({ loading: false, data: null }));
      } catch {
        setSingleShopInfo({ loading: false, data: null });
      }
    },
    [token]
  );

  const getProductsOptions = useCallback(async (val?: string) => {
    setProductsOptions({ loading: true, list: [] });

    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_READER_API}/admin/product/v2?shopId=${visibleData?.shopId}` +
        (val ? `&key=${val}` : ``),

      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setProductsOptions({
      loading: false,
      list: res?.data?.products?.map((product: any) => {
        return {
          value: product?.id,
          label: product?.name,
        };
      }),
    });
  }, []);
  useEffect(() => {
    getProductsOptions();
  }, [getProductsOptions]);
  useEffect(() => {
    fetchShopDetails(visibleData?.shopId);
  }, [fetchShopDetails, visibleData?.shopId]);

  useEffect(() => {
    const run = async () => {
      if (!visibleData?.category?.id) return;

      try {
        const resp = await fetch(
          `${process.env.REACT_APP_CATALOG_READER_API}/admin/category/detail?categoryId=${visibleData?.category?.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const res = await resp.json();
        if (res.statusCode === 200) {
          setCategoryData(res?.category);
          setCatCommissionAmount(res?.category?.commissionAmount);
          setCatCommissionType(res?.category?.commissionType);
        } else {
          responseNotification(res.message || "something wrong", "warning");
        }
      } catch (err) {
        responseNotification(`Internal server error ${err}`, "error");
        console.error(err);
      }
    };

    run();
  }, [visibleData?.category?.id, token]);

  const onSubmit = async (data: any) => {
    setLoading(true);

    const readyData = {
      id: visibleData?.id,
      variations: (data?.variations || []).map((v: any) => ({
        barCode: v.barCode,
        variationName: v.variationName,
        price: {
          mrp: parseFloat(v.mrp),
          tp: parseFloat(v.tp),
          discountedPrice: parseFloat(v.sellerPrice),
        },
        productDiscount: {
          flat: parseFloat(v.flat) || 0,
          percentage: parseFloat(v.percentage) || 0,
        },
        specialDiscountPrice: v?.isSpecialDiscount
          ? {
              discountPrice: parseFloat(v.specialSellPrice) || 0,
              startAt: v.startAt || null,
              endAt: v.endAt || null,
            }
          : null,
        quantitativeProductDiscount: {
          freeProductId: freeProductId || null,
          minimumQuantity: parseFloat(v.minimumQuantity) || 0,
          productDiscount: {
            flat: parseFloat(v.qtyFlat) || 0,
            percentage: parseFloat(v.qtyPercentage) || 0,
          },
        },
        stock: parseFloat(v.stock) || 0,
      })),
    };

    await fetch(`${process.env.REACT_APP_CATALOG_WRITER_API}/product`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...readyData, type }),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);

        if (res.statusCode === 200) {
          responseNotification("Product Updated Successfully", "success");
          onCloseMethod && onCloseMethod();
        } else {
          responseNotification(res.message || "Something Wrong", "warning");
        }
      })
      .catch(() => {
        setLoading(false);
        responseNotification("Internal server error", "error");
      });
  };

  const fetchRef = useRef(0);
  const handleSearch = React.useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      if (fetchId !== fetchRef.current) {
        return;
      }
      if (type) {
        if (value) {
          if (field === "product") getProductsOptions(value);
        }
      } else {
        responseNotification("Select a type before search", "warning");
      }
    };

    return debounce(loadOptions, 800);
  }, [getProductsOptions, type]);

  useEffect(() => {
    if (!visibleData?.variations?.length) return;

    const initialDiscountTypes: DiscountMode[] = {};
    const initialQtyDiscountTypes: any = {};
    const initialSelectedFreeProducts: { [key: number]: FreeProductItem } = {};

    const initialVariations = visibleData.variations.map(
      (v: any, index: number) => {
        const mrp = parseFloat(v?.price?.mrp) || 0;
        const flat = parseFloat(v?.productDiscount?.flat) || 0;
        const percentage = parseFloat(v?.productDiscount?.percentage) || 0;

        let mode: DiscountMode = null;
        if (flat > 0) mode = "disFlat";
        else if (percentage > 0) mode = "disPercentage";

        const sales = computeSalesPrice(mrp, flat, percentage, mode);

        const initialTp =
          basePriceCalculationType === "TP_BASED_COMMISSION"
            ? parseFloat(v?.price?.tp) || 0
            : computeTpFromSalesPrice(sales);

        const qtyFlat =
          parseFloat(v?.quantitativeProductDiscount?.productDiscount?.flat) ||
          0;
        const qtyPercentage =
          parseFloat(
            v?.quantitativeProductDiscount?.productDiscount?.percentage
          ) || 0;
        const minimumQuantity =
          parseFloat(v?.quantitativeProductDiscount?.minimumQuantity) || 0;

        let qtyType: "freeProduct" | "qtyFlat" | "qtyPercentage" | null = null;

        if (v?.quantitativeProductDiscount?.freeProductId) {
          qtyType = "freeProduct";
          initialSelectedFreeProducts[index] = {
            id: v?.quantitativeProductDiscount?.freeProductId,
            name:
              v?.quantitativeProductDiscount?.freeProductName ||
              "Selected Product",
            productImage:
              v?.quantitativeProductDiscount?.freeProductImage || "",
          };
        } else if (qtyFlat > 0) {
          qtyType = "qtyFlat";
        } else if (qtyPercentage > 0) {
          qtyType = "qtyPercentage";
        }

        initialQtyDiscountTypes[index] = {
          type: qtyType,
          value:
            qtyType === "freeProduct"
              ? v?.quantitativeProductDiscount?.freeProductId
              : qtyType === "qtyFlat"
              ? qtyFlat
              : qtyType === "qtyPercentage"
              ? qtyPercentage
              : undefined,
          minimumQuantity,
        };

        return {
          barCode: v.barCode,
          variationName: v.variationName,
          variationId: v.variationId,
          maximumOrderLimit: v.maximumOrderLimit,
          mrp: mrp,
          tp: initialTp.toFixed(2),
          stock: v.stock,
          sellerPrice: sales.toFixed(2),
          flat: flat,
          percentage: percentage,
          isSpecialDiscount: !!v?.specialDiscountPrice?.discountPrice,
          specialSellPrice: v?.specialDiscountPrice?.discountPrice || "",
          startAt: v?.specialDiscountPrice?.startAt || "",
          endAt: v?.specialDiscountPrice?.endAt || "",
          minimumQuantity: minimumQuantity || "",
          qtyFlat: qtyFlat || "",
          qtyPercentage: qtyPercentage || "",
          freeProductId: v?.quantitativeProductDiscount?.freeProductId || "",
        };
      }
    );

    setDiscountTypes(
      Array.isArray(initialDiscountTypes)
        ? initialDiscountTypes
        : visibleData.variations.map((v: any) => {
            const flat = parseFloat(v?.productDiscount?.flat) || 0;
            const percentage = parseFloat(v?.productDiscount?.percentage) || 0;
            if (flat > 0) return "disFlat";
            if (percentage > 0) return "disPercentage";
            return null;
          })
    );

    setQtyDiscountType(initialQtyDiscountTypes);
    setSelectedFreeProducts(initialSelectedFreeProducts);
    form.setFieldsValue({ variations: initialVariations });
  }, [visibleData, form, basePriceCalculationType]);

  const handleAdd = () => {
    const current = form.getFieldValue("variations") || [];

    const defaultSales = 0;
    const defaultTp =
      basePriceCalculationType === "TP_BASED_COMMISSION"
        ? 0
        : computeTpFromSalesPrice(defaultSales);

    form.setFieldsValue({
      variations: [
        ...current,
        {
          barCode: "",
          variationName: "",
          variationId: undefined,
          maximumOrderLimit: undefined,
          mrp: 0,
          tp: defaultTp.toFixed(2),
          stock: 0,
          sellerPrice: defaultSales.toFixed(2),
          flat: 0,
          percentage: 0,
          isSpecialDiscount: false,
          specialSellPrice: "",
          startAt: "",
          endAt: "",
          minimumQuantity: "",
          qtyFlat: "",
          qtyPercentage: "",
          freeProductId: "",
        },
      ],
    });

    setDiscountTypes((prev) => [...prev, null]);
  };

  const handleRemove = (index: number, removeFn: (name: number) => void) => {
    removeFn(index);

    setDiscountTypes((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });

    setQtyDiscountType((prev) => {
      const next = { ...prev };
      delete next[index];

      const reIndexed: any = {};
      Object.keys(next).forEach((key) => {
        const oldIndex = Number(key);
        reIndexed[oldIndex > index ? oldIndex - 1 : oldIndex] = next[key];
      });
      return reIndexed;
    });

    setSelectedFreeProducts((prev) => {
      const next = { ...prev };
      delete next[index];

      const reIndexed: any = {};
      Object.keys(next).forEach((key) => {
        const oldIndex = Number(key);
        reIndexed[oldIndex > index ? oldIndex - 1 : oldIndex] = next[key];
      });
      return reIndexed;
    });

    setTimeout(() => {
      const variations = form.getFieldValue("variations") || [];
      variations.forEach((_: any, i: number) => recalcRow(i));
    }, 0);
  };

  const handleValuesChange = (changedValues: any) => {
    if (!changedValues?.variations) return;

    const changedVariations = changedValues.variations;

    let idx = -1;
    let changedRow: any = null;

    if (Array.isArray(changedVariations)) {
      idx = changedVariations.findIndex((v: any) => v);
      changedRow = idx >= 0 ? changedVariations[idx] : null;
    } else if (
      typeof changedVariations === "object" &&
      changedVariations !== null
    ) {
      const keys = Object.keys(changedVariations);
      if (keys.length > 0) {
        idx = Number(keys[0]);
        changedRow = changedVariations[keys[0]];
      }
    }

    if (idx < 0 || !changedRow) return;

    if (Object.prototype.hasOwnProperty.call(changedRow, "tp")) {
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(changedRow, "isSpecialDiscount") ||
      Object.prototype.hasOwnProperty.call(changedRow, "specialSellPrice") ||
      Object.prototype.hasOwnProperty.call(changedRow, "startAt") ||
      Object.prototype.hasOwnProperty.call(changedRow, "endAt")
    ) {
      return;
    }

    recalcRow(idx);
  };

  const openFreeProductModal = (rowIndex: number) => {
    setFreeProductModal({
      open: true,
      rowIndex,
    });
  };

  const closeFreeProductModal = () => {
    setFreeProductModal({
      open: false,
      rowIndex: null,
    });
  };

  const handleSelectFreeProduct = (productId: string) => {
    const rowIndex = freeProductModal.rowIndex;
    if (rowIndex === null || rowIndex === undefined) return;

    const selected = freeProductOptions.find((item) => item.id === productId);
    if (!selected) return;

    setSelectedFreeProducts((prev) => ({
      ...prev,
      [rowIndex]: selected,
    }));

    const variations = [...(form.getFieldValue("variations") || [])];
    if (variations[rowIndex]) {
      variations[rowIndex].freeProductId = selected.id;
      form.setFieldsValue({ variations });
    }

    setQtyDiscountType((prev: any) => ({
      ...prev,
      [rowIndex]: {
        ...(prev?.[rowIndex] || {}),
        type: "freeProduct",
        value: selected.id,
        minimumQuantity: prev?.[rowIndex]?.minimumQuantity,
      },
    }));

    closeFreeProductModal();
  };

  const handleRemoveFreeProduct = (rowIndex: number) => {
    setSelectedFreeProducts((prev) => ({
      ...prev,
      [rowIndex]: undefined,
    }));

    const variations = [...(form.getFieldValue("variations") || [])];
    if (variations[rowIndex]) {
      variations[rowIndex].freeProductId = "";
      form.setFieldsValue({ variations });
    }
  };

  return (
    <>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        onValuesChange={handleValuesChange}
      >
        <Form.List name="variations">
          {(fields, { remove }) => (
            <>
              {fields.map(({ key, name, ...restField }: any) => (
                <Card
                  key={key}
                  title={
                    visibleData?.variations?.[name]?.variationName ||
                    `Variation ${name + 1}`
                  }
                  className="mb-4"
                  extra={
                    <Button
                      danger
                      type="text"
                      onClick={() => handleRemove(name, remove)}
                    >
                      <CloseOutlined />
                    </Button>
                  }
                >
                  <Space wrap align="center">
                    <Form.Item
                      {...restField}
                      name={[name, "barCode"]}
                      label="Barcode"
                      style={{ width: 120 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "variationName"]}
                      label="Variation"
                      rules={[{ required: true }]}
                      style={{ width: 140 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "mrp"]}
                      label="MRP"
                      rules={[{ required: true }]}
                      style={{ width: 90 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "tp"]}
                      label="TP"
                      style={{ width: 90 }}
                      rules={
                        basePriceCalculationType === "TP_BASED_COMMISSION"
                          ? [{ required: true, message: "TP required" }]
                          : undefined
                      }
                    >
                      <Input
                        disabled={
                          basePriceCalculationType !== "TP_BASED_COMMISSION"
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "stock"]}
                      label="Stock"
                      rules={[{ required: true }]}
                      style={{ width: 90 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "sellerPrice"]}
                      label="Sales Price"
                      rules={[{ required: true }]}
                      style={{ width: 120 }}
                    >
                      <Input readOnly />
                    </Form.Item>
                  </Space>

                  <div className="mt-4 mb-3">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="bg-green-500 rounded text-white px-2 font-bold h-8 flex items-center">
                        Special Discount
                      </div>

                      <Form.Item
                        {...restField}
                        name={[name, "isSpecialDiscount"]}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Switch
                          checkedChildren="Allow"
                          unCheckedChildren="Not Allow"
                        />
                      </Form.Item>
                    </div>

                    <Form.Item
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues?.variations?.[name]?.isSpecialDiscount !==
                        curValues?.variations?.[name]?.isSpecialDiscount
                      }
                      noStyle
                    >
                      {({ getFieldValue }) =>
                        getFieldValue([
                          "variations",
                          name,
                          "isSpecialDiscount",
                        ]) ? (
                          <div className="flex items-end gap-4 flex-wrap mt-4">
                            <Form.Item
                              {...restField}
                              name={[name, "specialSellPrice"]}
                              label="Special Sale Price"
                              style={{ width: 180, marginBottom: 0 }}
                            >
                              <Input
                                id="specialSellPrice"
                                type="number"
                                min={0}
                                placeholder="Special Sale Price"
                              />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              name={[name, "startAt"]}
                              label="Start Date"
                              style={{ width: 160, marginBottom: 0 }}
                            >
                              <Input
                                id="startAt"
                                type="date"
                                placeholder="Enter Date"
                              />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              name={[name, "endAt"]}
                              label="End Date"
                              style={{ width: 160, marginBottom: 0 }}
                            >
                              <Input
                                id="endAt"
                                type="date"
                                placeholder="Enter Date"
                              />
                            </Form.Item>
                          </div>
                        ) : null
                      }
                    </Form.Item>
                  </div>

                  <Space wrap align="center" className="mb-3 mt-4">
                    <h6 className="bg-green-500 rounded text-white px-2 font-bold">
                      General Discount
                    </h6>
                    <Radio.Group
                      value={discountType[name]?.type}
                      onChange={(e: any) => {
                        setDiscountType((prev: any) => ({
                          ...prev,
                          [name]: {
                            type: e.target.value,
                            value: undefined,
                          },
                        }));

                        setDiscountTypes((prev) => {
                          const next = [...prev];
                          next[name] =
                            e.target.value === undefined
                              ? null
                              : e.target.value;
                          return next;
                        });

                        setTimeout(() => {
                          recalcRow(name, e.target.value ?? null);
                        }, 0);
                      }}
                      name="productDiscount"
                    >
                      <Radio value={undefined}>None</Radio>
                      <Radio value="disFlat">Flat</Radio>
                      <Radio value="disPercentage">Percentage</Radio>
                    </Radio.Group>

                    {discountType[name]?.type === "disFlat" && (
                      <Form.Item
                        {...restField}
                        name={[name, "flat"]}
                        initialValue={discountType?.[name]?.value}
                        hasFeedback
                        style={{ width: 100, marginBottom: 0 }}
                      >
                        <Input placeholder="Flat (Tk.)" size="small" />
                      </Form.Item>
                    )}

                    {discountType?.[name]?.type === "disPercentage" && (
                      <Form.Item
                        {...restField}
                        name={[name, "percentage"]}
                        hasFeedback
                        initialValue={discountType?.[name]?.value}
                        style={{ width: 100, marginBottom: 0 }}
                      >
                        <Input placeholder="Percent (%)" size="small" />
                      </Form.Item>
                    )}
                  </Space>

                  <Space wrap key={key} align="center" className="mt-4">
                    <h6 className="bg-green-500 rounded text-white px-2 font-bold">
                      Quantitive Discount
                    </h6>

                    <Radio.Group
                      value={qtyDiscountType?.[name]?.type}
                      onChange={(val) => {
                        const selectedType = val.target.value;

                        setQtyDiscountType((prev: any) => ({
                          ...prev,
                          [name]: {
                            type: selectedType ?? null,
                            value: undefined,
                            minimumQuantity:
                              prev?.[name]?.minimumQuantity || undefined,
                          },
                        }));

                        if (selectedType !== "freeProduct") {
                          handleRemoveFreeProduct(name);
                        }
                      }}
                      name="quantitiveProductDiscount"
                    >
                      <Radio value={undefined}>None</Radio>
                      <Radio value="qtyFlat">Flat</Radio>
                      <Radio value="qtyPercentage">Percentage</Radio>
                      <Radio value="freeProduct">Free Product</Radio>
                    </Radio.Group>

                    {qtyDiscountType?.[name]?.type !== null &&
                      qtyDiscountType?.[name]?.type !== undefined && (
                        <Form.Item
                          {...restField}
                          name={[name, "minimumQuantity"]}
                          hasFeedback
                          initialValue={
                            qtyDiscountType?.[name]?.minimumQuantity
                          }
                          style={{ width: 120, marginBottom: 0 }}
                        >
                          <Input placeholder="Min Quantity" size="small" />
                        </Form.Item>
                      )}

                    <Form.Item
                      {...restField}
                      name={[name, "freeProductId"]}
                      style={{ display: "none" }}
                    >
                      <Input />
                    </Form.Item>

                    {qtyDiscountType?.[name]?.type === "freeProduct" &&
                      (selectedFreeProducts?.[name]?.id ? (
                        <div className="d-flex align-items-start">
                          <Avatar
                            src={
                              <Image
                                src={
                                  selectedFreeProducts?.[name]?.productImage ||
                                  discountImageUrl[name]
                                }
                              />
                            }
                            shape="square"
                            size="large"
                            className="border"
                          />
                          <div className="ml-2 mr-2">
                            <div style={{ fontWeight: 600 }}>
                              {selectedFreeProducts?.[name]?.name}
                            </div>
                            <Button
                              type="link"
                              size="small"
                              onClick={() => openFreeProductModal(name)}
                              style={{ padding: 0 }}
                            >
                              Change
                            </Button>
                          </div>
                          <Button
                            danger
                            type="text"
                            onClick={() => handleRemoveFreeProduct(name)}
                            size="large"
                          >
                            <CloseOutlined />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="dashed"
                          onClick={() => openFreeProductModal(name)}
                          size="large"
                        >
                          <GiftOutlined /> Free Product
                        </Button>
                      ))}

                    {qtyDiscountType?.[name]?.type === "qtyFlat" && (
                      <Form.Item
                        {...restField}
                        name={[name, "qtyFlat"]}
                        hasFeedback
                        initialValue={qtyDiscountType?.[name]?.value}
                        style={{ width: 100, marginBottom: 0 }}
                      >
                        <Input placeholder="Flat (Tk.)" size="small" />
                      </Form.Item>
                    )}

                    {qtyDiscountType?.[name]?.type === "qtyPercentage" && (
                      <Form.Item
                        {...restField}
                        name={[name, "qtyPercentage"]}
                        hasFeedback
                        initialValue={qtyDiscountType?.[name]?.value}
                        style={{ width: 100, marginBottom: 0 }}
                      >
                        <Input placeholder="Percent (%)" size="small" />
                      </Form.Item>
                    )}
                  </Space>
                </Card>
              ))}

              <Button
                type="primary"
                danger
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Add More
              </Button>
            </>
          )}
        </Form.List>

        <Divider />
        <SubmitResetBtn loading={loading} />
      </Form>

      <Modal
        open={freeProductModal.open}
        onCancel={closeFreeProductModal}
        footer={null}
        title="Select Free Product"
        destroyOnClose
      >
        <Form.Item hasFeedback name="productId">
          <Select
            showSearch
            placeholder="Select Product"
            optionFilterProp="children"
            onSearch={(val) => {
              handleSearch(val, "product");
            }}
            onChange={(val) => setFreeProductId(val)}
            filterOption={() => {
              return true;
            }}
            options={productsOptions?.list}
          ></Select>
        </Form.Item>
      </Modal>
    </>
  );
};

export default QuickUpdate;
